import React, { useState, useMemo } from 'react';
import { 
  Drawer, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  Toolbar,
  Divider,
  Collapse,
  Box,
  Tooltip,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import InventoryIcon from '@mui/icons-material/Inventory';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import SecurityIcon from '@mui/icons-material/Security';
import BusinessIcon from '@mui/icons-material/Business';
import HandymanIcon from '@mui/icons-material/Handyman';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import HomeIcon from '@mui/icons-material/Home';
import CategoryIcon from '@mui/icons-material/Category';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import EngineeringIcon from '@mui/icons-material/Engineering';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { isAdminRole } from '../../consts/roles';

const drawerWidth = 240;
const collapsedWidth = 64;

interface SidebarProps {
  open: boolean;
}

interface MenuOption {
  text: string;
  icon: React.ReactNode;
  path: string;
  permissions?: string[];
  anyPermissions?: string[];
  requireAdmin?: boolean;
  subItems?: MenuOption[];
}

const Sidebar: React.FC<SidebarProps> = ({ open }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Inicializar submenús abiertos si la ruta actual coincide
  const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {
      'dashboard/users': location.pathname.startsWith('/dashboard/users'),
    };
    return initial;
  });
  
  // Obtener permisos y usuario directamente del estado Redux
  const { permissions: userPermissions, isSuperAdmin, user } = useSelector((state: RootState) => state.auth);

  const handleToggleSubmenu = (key: string) => {
    setOpenSubmenus(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isSelected = (path: string) => {
    return location.pathname === path;
  };

  const isSubmenuActive = (parentPath: string) => {
    return location.pathname.startsWith(parentPath);
  };
  
  // Crear funciones de verificación de permisos
  const hasPermission = (permissionCode: string) => {
    // Si es superadmin, tiene todos los permisos
    if (isSuperAdmin) return true;
    
    // Verificar si el usuario tiene el permiso específico
    return userPermissions.includes(permissionCode);
  };
  
  const hasAnyPermission = (permissionCodes: string[]) => {
    // Si es superadmin, tiene todos los permisos
    if (isSuperAdmin) return true;
    
    // Verificar si el usuario tiene al menos uno de los permisos
    return permissionCodes.some(code => userPermissions.includes(code));
  };

  // Definir las opciones del menú con sus permisos requeridos
  const roleName = user?.role?.name;
  const isAdmin = isAdminRole(roleName);
  const defaultDashboard = user?.role?.defaultDashboard;
  
  // Determinar la ruta de inicio según el defaultDashboard del rol
  const getHomePath = () => {
    if (defaultDashboard) {
      return defaultDashboard;
    }
    // Fallback: si es admin, ir al dashboard; si no, a home
    return isAdmin ? '/dashboard' : '/home';
  };
  
  const menuOptions: MenuOption[] = [
    {
      text: isAdmin ? 'Dashboard' : 'Inicio',
      icon: isAdmin ? <DashboardIcon /> : <HomeIcon />,
      path: getHomePath(),
    },
    {
      text: 'Gestión de Usuarios',
      icon: <PeopleIcon />,
      path: '/dashboard/users',
      permissions: ['users:read'],
      subItems: [
        {
          text: 'Usuarios',
          icon: <PersonIcon />,
          path: '/dashboard/users',
          permissions: ['users:read'],
        },
        {
          text: 'Roles',
          icon: <BadgeIcon />,
          path: '/dashboard/users/roles',
          permissions: ['roles:read'],
        },
        {
          text: 'Permisos',
          icon: <SecurityIcon />,
          path: '/dashboard/users/permissions',
          permissions: ['permissions:read'],
        },
      ],
    },
    {
      text: 'Clientes',
      icon: <BusinessIcon />,
      path: '/customers',
      permissions: ['customers:read']
    },
    {
      text: 'Personas',
      icon: <PeopleIcon />,
      path: '/persons',
      permissions: ['persons:read']
    },
    {
      text: 'Equipos',
      icon: <HandymanIcon />,
      path: '/equipment',
      permissions: ['equipment:read']
    },
    {
      text: 'Consolidado',
      icon: <ReceiptIcon />,
      path: '/orders',
      permissions: ['orders:read']
    },
    {
      text: 'Migración de Consolidado',
      icon: <CloudUploadIcon />,
      path: '/file-migration',
      anyPermissions: ['file-migration:upload', 'file-migration:reset'],
    },
    {
      text: 'Inventario',
      icon: <InventoryIcon />,
      path: '/inventory',
      permissions: ['inventory:read'],
    },
    {
      text: 'Proveedores',
      icon: <BusinessIcon />,
      path: '/inventory/suppliers',
      permissions: ['inventory:read'],
    },
    {
      text: 'Categorías',
      icon: <CategoryIcon />,
      path: '/inventory/categories',
      permissions: ['inventory:read'],
    },
    {
      text: 'Ubicaciones',
      icon: <LocationOnIcon />,
      path: '/inventory/locations',
      permissions: ['inventory:read'],
    },
    {
      text: 'Trabajadores',
      icon: <WorkIcon />,
      path: '/inventory/workers',
      permissions: ['inventory:read'],
      requireAdmin: true,
    },
    {
      text: 'Migración de Inventario',
      icon: <UploadFileIcon />,
      path: '/inventory/migration',
      permissions: ['inventory:read'],
    },
    {
      text: 'Revisar Solicitudes',
      icon: <CheckCircleIcon />,
      path: '/inventory/requests/approval',
      permissions: ['inventory:read-request'],
    },
    {
      text: 'Mis Items',
      icon: <Inventory2Icon />,
      path: '/production/items',
      permissions: ['production:read-request'],
    },
    {
      text: 'Mis Solicitudes',
      icon: <RequestQuoteIcon />,
      path: '/production/requests/list',
      permissions: ['production:read-request'],
    },
    {
      text: 'Nueva Solicitud',
      icon: <RequestQuoteIcon />,
      path: '/production/requests',
      permissions: ['production:create-request'],
    },
    {
      text: 'Nueva Devolución',
      icon: <AssignmentReturnIcon />,
      path: '/production/returns',
      permissions: ['production:create-return'],
    },
    {
      text: 'Ingeniería',
      icon: <EngineeringIcon />,
      path: '/engineering',
      permissions: ['bom:read'],
    },
    // {
    //   text: 'Configuración',
    //   icon: <SettingsIcon />,
    //   path: '/dashboard/settings',
    //   permissions: ['VER_CONFIGURACION'],
    // },
  ];

  // Filtrar las opciones del menú según los permisos del usuario - usando useMemo
  const filterMenuOptions = (options: MenuOption[]): MenuOption[] => {
    return options.filter(option => {
      // Si no tiene permisos definidos, mostrar siempre
      if (!option.permissions && !option.anyPermissions) {
        return true;
      }

      // Verificar si tiene todos los permisos requeridos
      if (option.permissions && option.permissions.length > 0) {
        const hasAllRequiredPermissions = option.permissions.every(p => hasPermission(p));
        if (!hasAllRequiredPermissions) return false;
      }

      // Verificar si tiene al menos uno de los permisos alternativos
      if (option.anyPermissions && option.anyPermissions.length > 0) {
        const hasAnyRequiredPermission = hasAnyPermission(option.anyPermissions);
        if (!hasAnyRequiredPermission) return false;
      }

      // Verificar si requiere ser admin o super admin
      if (option.requireAdmin) {
        if (!isSuperAdmin && !isAdmin) return false;
      }

      // Si tiene submenú, filtrar también sus opciones
      if (option.subItems) {
        const filteredSubItems = filterMenuOptions([...option.subItems]);
        // Si no queda ningún submenú, ocultar la opción principal
        if (filteredSubItems.length === 0) {
          return false;
        }
        // Asignar los subitems filtrados al objeto actual
        option = { ...option, subItems: filteredSubItems };
      }

      return true;
    });
  };

  // Obtener las opciones filtradas usando useMemo para evitar recálculos innecesarios
  const filteredMenuOptions = useMemo(() => {
    return filterMenuOptions(menuOptions);
  }, [userPermissions, isSuperAdmin, isAdmin]); // Solo recalcular cuando cambian los permisos o el rol

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: open ? drawerWidth : collapsedWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        transition: (theme) =>
          theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: open
              ? theme.transitions.duration.enteringScreen
              : theme.transitions.duration.leavingScreen,
          }),
        '& .MuiDrawer-paper': {
          width: open ? drawerWidth : collapsedWidth,
          boxSizing: 'border-box',
          overflowX: 'hidden',
          transition: (theme) =>
            theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: open
                ? theme.transitions.duration.enteringScreen
                : theme.transitions.duration.leavingScreen,
            }),
          backgroundColor: '#1a1a1a', // Gris muy oscuro, casi negro
          color: '#f0f0f0',
          borderRight: '1px solid #333',
        },
      }}
    >
      <Toolbar 
        sx={{ 
          backgroundColor: '#212121',
          borderBottom: '1px solid #333'
        }}
      />
      <Divider 
        sx={{ 
          backgroundColor: '#333',
        }}
      />
      <List>
        {filteredMenuOptions.map((option) => (
          <Box key={option.text}>
            {option.subItems ? (
              <>
                <ListItem disablePadding>
                  <Tooltip
                    title={option.text}
                    placement="right"
                    disableHoverListener={open}
                  >
                    <ListItemButton
                      selected={isSubmenuActive(option.path)}
                      onClick={() =>
                        open ? handleToggleSubmenu(option.path) : navigate(option.path)
                      }
                      sx={{
                        justifyContent: open ? 'initial' : 'center',
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(117, 117, 117, 0.15)', // Gris plateado con opacidad
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(117, 117, 117, 0.1)',
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: '#9e9e9e',
                          minWidth: 0,
                          mr: open ? 3 : 'auto',
                          justifyContent: 'center',
                        }}
                      >
                        {option.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={option.text}
                        primaryTypographyProps={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '0.875rem',
                        }}
                        sx={{ opacity: open ? 1 : 0 }}
                      />
                      {open &&
                        (openSubmenus[option.path] ? (
                          <ExpandLess sx={{ color: '#9e9e9e' }} />
                        ) : (
                          <ExpandMore sx={{ color: '#9e9e9e' }} />
                        ))}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
                <Collapse in={open && openSubmenus[option.path]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {option.subItems.map((subItem) => (
                      <ListItemButton
                        key={subItem.text}
                        selected={isSelected(subItem.path)}
                        onClick={() => navigate(subItem.path)}
                        sx={{ 
                          pl: 4,
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(117, 117, 117, 0.15)',
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(117, 117, 117, 0.1)',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ 
                          color: subItem.text === 'Permisos' ? '#d32f2f' : '#9e9e9e' 
                        }}>
                          {subItem.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={subItem.text}
                          primaryTypographyProps={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.875rem',
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </>
            ) : (
              <ListItem disablePadding>
                <Tooltip
                  title={option.text}
                  placement="right"
                  disableHoverListener={open}
                >
                  <ListItemButton
                    selected={isSelected(option.path)}
                    onClick={() => navigate(option.path)}
                    sx={{
                      justifyContent: open ? 'initial' : 'center',
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(117, 117, 117, 0.15)',
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(117, 117, 117, 0.1)',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: '#9e9e9e',
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      {option.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={option.text}
                      primaryTypographyProps={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.875rem',
                      }}
                      sx={{ opacity: open ? 1 : 0 }}
                    />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            )}
          </Box>
        ))}
      </List>
    </Drawer>
  );
};

export default Sidebar; 